<template>
  <div
    v-if="isDisplayed && renderedSections && renderedSections.length > 0"
    ref="contextMenuEl"
    v-hotkey="shortcuts"
    v-click-outside="hide"
    class="right-click-menu"
    :style="style"
    @click.stop="hide"
  >
    <div ref="triggerEl" class="triggerEl" />
    <template
      v-for="(section, sectionIndex) in renderedSections"
      :key="sectionIndex"
    >
      <div class="section">
        <component
          v-for="(item, itemIndex) in section"
          :key="itemIndex"
          :is="item.component"
          v-bind="item.props"
          @click="item.onClick"
          @select="item.onSelect"
        >
          <template v-if="item.iconSlot" #icon>
            <component :is="item.iconSlot" />
          </template>
          <template v-if="item.rightSlot" #right>
            {{ item.rightSlot }}
          </template>
        </component>
      </div>
      <div
        v-if="renderedSections && renderedSections.length > sectionIndex + 1"
        class="separator"
      />
    </template>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
  type Component,
} from "vue";
import { useRoute } from "vue-router";

import { Recurrence, Week } from "@ctrl/controller-rpc";

import { taskActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import AttendanceButtons from "@/components/context-menu/AttendanceButtons.vue";
import {
  BlockContext,
  ContextCancellable,
  getActions,
  RunnableContextAction,
} from "@/components/context-menu/context-actions";
import * as inject from "@/components/inject";
import { setPosition } from "@/components/popup";
import { assignShortcuts, closeModal, Shortcuts } from "@/components/shortcuts";
import { useOverlayPresence } from "@/composables/useOverlayPresence";
import { planTask } from "@/task";
import { makeMove } from "@/utils/move";
import { navigateToObject } from "@/utils/navigate";

import { useEventInfo } from "../eventInfo";

export default defineComponent({
  components: { BaseButton, AttendanceButtons },
  setup() {
    const console = inject.console();
    const controller = inject.controller();
    const profile = inject.profile();
    const multipleEventsModal = inject.multipleEventsModal();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const logger = inject.logger();
    const destinationPicker = inject.destinationPicker();
    const move = makeMove(controller, profile, logger, destinationPicker);
    const router = inject.router();
    const simpleModal = inject.simpleModal();
    const menu = inject.menu();
    const automations = inject.automations();
    const route = useRoute();
    const contextMenuEl = ref<HTMLElement>();
    const triggerEl = ref<HTMLElement>();
    // Invisible persistent anchor used to position popups (when opened from context menu)
    // right next to where the click happened
    const anchorEl = ref<HTMLElement>();
    type MenuObjectContext = Exclude<ContextCancellable, BlockContext>;
    type MenuState =
      | {
          kind: "block";
          context: BlockContext;
          actions: RunnableContextAction[];
        }
      | {
          kind: "object";
          context: MenuObjectContext;
          actions: RunnableContextAction[];
        };
    const menuState = shallowRef<MenuState | null>(null);
    const isMeetingWithMe = ref<boolean>(false);
    const isDisplayed = ref(false);
    const releaseOverlayPresence = useOverlayPresence(isDisplayed);
    onBeforeUnmount(releaseOverlayPresence);
    const top = ref(0);
    const left = ref(0);
    const style = computed<string>(
      () => `top:${top.value}px; left: ${left.value}px`
    );
    const hide = () => (isDisplayed.value = false);

    // Robust close behaviors for contexts that stop propagation (e.g. editor).
    const onGlobalKeyDownCapture = (event: KeyboardEvent) => {
      if (!isDisplayed.value) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        hide();
      }
    };
    const onGlobalPointerDownCapture = (event: PointerEvent) => {
      if (!isDisplayed.value) return;
      const menuEl = contextMenuEl.value;
      const target =
        event.target && event.target instanceof globalThis.Node
          ? event.target
          : null;
      if (!menuEl || !target) return;
      if (menuEl.contains(target)) return;
      hide();
    };
    watch(
      isDisplayed,
      (shown) => {
        if (shown) {
          window.addEventListener("keydown", onGlobalKeyDownCapture, true);
          window.addEventListener(
            "pointerdown",
            onGlobalPointerDownCapture,
            true
          );
        } else {
          window.removeEventListener("keydown", onGlobalKeyDownCapture, true);
          window.removeEventListener(
            "pointerdown",
            onGlobalPointerDownCapture,
            true
          );
        }
      },
      { immediate: true }
    );
    // Whether the current menu was opened from a block inside a notes
    // editor: actions that create an object next to the original (e.g.
    // "Duplicate") must then not navigate away from the document.
    const suppressNavigation = ref(false);
    const show = async (
      object: ContextCancellable,
      mouseEvent: MouseEvent,
      options?: { suppressNavigation?: boolean }
    ) => {
      suppressNavigation.value = options?.suppressNavigation ?? false;
      if (object.type === "block") {
        const actions = await getActions(
          controller,
          profile.value,
          menu,
          object
        );
        menuState.value = {
          kind: "block",
          context: object,
          actions: actions.map((action) => ({
            ...action,
            icon: markRaw(action.icon),
          })),
        };
      } else {
        const actions = await getActions(
          controller,
          profile.value,
          menu,
          object
        );
        menuState.value = {
          kind: "object",
          context: object,
          actions: actions.map((action) => ({
            ...action,
            icon: markRaw(action.icon),
          })),
        };
      }

      // Get event info if this is an event
      if (object.type === "event") {
        const eventRef = ref(object.value);
        const eventInfo = await useEventInfo(controller, undefined, eventRef);
        isMeetingWithMe.value = eventInfo.isMeetingWithMe.value;
      } else {
        isMeetingWithMe.value = false;
      }

      isDisplayed.value = true;
      await nextTick();
      const menuEl = contextMenuEl.value;
      const coord = setPosition(
        menuEl?.offsetWidth || 0,
        menuEl?.offsetHeight || 0,
        mouseEvent.clientX,
        mouseEvent.clientY
      );
      top.value = coord.top;
      left.value = coord.left;
      if (anchorEl.value) {
        anchorEl.value.style.left = `${mouseEvent.clientX}px`;
        anchorEl.value.style.top = `${mouseEvent.clientY}px`;
      }
    };
    const onSelectPlanDate = (
      data: {
        mode: "date" | "week" | "month";
        date?: Date;
        week?: Week;
      } | null
    ) => {
      if (
        menuState.value?.kind === "object" &&
        menuState.value.context.type === "task"
      ) {
        // "Clear" (null) unplans the task.
        if (data === null) {
          taskActions.unplan.action(
            { type: "task", value: menuState.value.context.value },
            { controller }
          );
          return;
        }
        const planned = data.mode === "date" ? data.date : data.week;
        if (planned !== undefined)
          planTask(
            controller,
            menuState.value.context.value.id,
            planned,
            "app"
          );
      }
      datePicker.value.closePicker();
    };
    const onSelectRecurrence = async (recurrence: Recurrence) => {
      // If the recurrence is applied on a task, create a new recurring task
      if (
        menuState.value?.kind === "object" &&
        menuState.value.context.type === "task"
      ) {
        recurrencePicker.value.closePicker();

        const task = menuState.value.context.value;
        const recurrentTaskId = await controller.taskRecurrent.create(
          {
            title: task.title,
            parent: task.parent || null,
            recurrence,
          },
          { notify: true }
        );
        await navigateToObject(router, controller, {
          kind: "task-rec",
          id: recurrentTaskId,
        });
      }
      // If the recurrence is applied on a recurring task, update the rule
      else if (
        menuState.value?.kind === "object" &&
        menuState.value.context.type === "task-rec"
      ) {
        recurrencePicker.value.closePicker();
        await controller.taskRecurrent.update(
          menuState.value.context.value.id,
          {
            recurrence,
          },
          "app"
        );
      }
    };
    const datePicker = inject.datePicker();
    const recurrencePicker = inject.recurrencePicker();
    const assigneePicker = inject.assigneePicker();
    const palette = inject.palette();

    const buildActionInject = (clickEvent?: MouseEvent) => ({
      console: console.value,
      controller,
      profile,
      logger,
      move,
      flush: () => {},
      multipleEventsModal,
      route,
      router,
      simpleModal,
      menu,
      automations,
      palette,
      clickEvent,
      suppressNavigation: suppressNavigation.value || undefined,
      datePickerInject: {
        component: datePicker,
        triggerEl: anchorEl,
        onSelect: onSelectPlanDate,
      },
      recurrencePickerInject: {
        component: recurrencePicker,
        triggerEl: anchorEl,
        onSelect: onSelectRecurrence,
      },
      destinationPickerInject: {
        triggerEl: anchorEl,
      },
      assigneePickerInject: {
        component: assigneePicker,
        triggerEl: anchorEl,
      },
    });

    const runObjectAction = (
      action: RunnableContextAction,
      clickEvent?: MouseEvent
    ) => {
      const state = menuState.value;
      if (!state || state.kind !== "object") return null;
      return action.run(buildActionInject(clickEvent));
    };

    const runBlockAction = (
      action: RunnableContextAction,
      clickEvent?: MouseEvent
    ) => {
      const state = menuState.value;
      if (!state || state.kind !== "block") return null;
      return action.run(buildActionInject(clickEvent));
    };

    const shortcuts = computed<Shortcuts>(() => {
      return isDisplayed.value
        ? {
            ...assignShortcuts(
              { isModalShown, isInputFocused },
              closeModal,
              hide
            ),
          }
        : {};
    });

    const currentAttendance = computed(() => {
      const state = menuState.value;
      if (state?.kind === "object" && state.context.type === "event") {
        return state.context.value.willAttend;
      }
      return null;
    });

    const renderedSections = computed(() => {
      const state = menuState.value;
      if (!state) return undefined;

      const groupActions = <T extends { group?: "top" | "middle" | "bottom" }>(
        actions: T[]
      ): T[][] => {
        const topGroup = actions.filter((action) => action.group === "top");
        const midGroup = actions.filter(
          (action) => !action.group || action.group === "middle"
        );
        const bottomGroup = actions.filter(
          (action) => action.group === "bottom"
        );
        const grouped: T[][] = [];
        if (topGroup.length > 0) grouped.push(topGroup);
        if (midGroup.length > 0) grouped.push(midGroup);
        if (bottomGroup.length > 0) grouped.push(bottomGroup);
        return grouped;
      };

      if (state.kind === "block") {
        const groups = groupActions<RunnableContextAction>(state.actions);
        if (groups.length === 0) return undefined;

        return groups.map((sectionActions) => {
          const items: Array<{
            component: Component;
            props: Record<string, unknown>;
            onClick?: (e: MouseEvent) => void;
            onSelect?: (action: RunnableContextAction) => void;
            iconSlot?: Component;
            rightSlot?: string;
          }> = [];

          for (const action of sectionActions) {
            items.push({
              component: BaseButton,
              props: {
                class: "context-action",
                kind: "auxiliary",
                text: action.text,
                state: action.state || "default",
                tint: action.tint || "none",
                isExpanded: true,
              },
              onClick: (clickEvent: MouseEvent) => {
                runBlockAction(action, clickEvent);
              },
              iconSlot: action.icon,
              rightSlot: action.shortcutsDisplay,
            });
          }

          return items;
        });
      }

      const groups = groupActions<RunnableContextAction>(state.actions);
      if (groups.length === 0) return undefined;

      return groups.map((sectionActions) => {
        const items: Array<{
          component: Component;
          props: Record<string, unknown>;
          onClick?: (e: MouseEvent) => void;
          onSelect?: (action: RunnableContextAction) => void;
          iconSlot?: Component;
          rightSlot?: string;
        }> = [];
        const attendanceActions: RunnableContextAction[] = [];

        for (const action of sectionActions) {
          if (action.text.startsWith("Attend: ")) {
            attendanceActions.push(action);
            continue;
          }
          items.push({
            component: BaseButton,
            props: {
              class: "context-action",
              kind: "auxiliary",
              text: action.text,
              state: action.state || "default",
              tint: action.tint || "none",
              isExpanded: true,
            },
            onClick: (clickEvent: MouseEvent) => {
              runObjectAction(action, clickEvent);
            },
            iconSlot: action.icon,
            rightSlot: action.shortcutsDisplay,
          });
        }

        if (attendanceActions.length > 0) {
          items.push({
            component: AttendanceButtons,
            props: {
              actions: attendanceActions,
              currentAttendance: currentAttendance.value,
            },
            onSelect: (action: RunnableContextAction) => {
              // Attendance buttons are only rendered for event objects.
              runObjectAction(action);
            },
          });
        }

        return items;
      });
    });

    onMounted(() => {
      // FIXME: possibly a bit hackish, to be reworked to better position
      // the pickers (date and recurrence pickers) when opened from context menu
      // by using a virtual anchor.
      const el = document.createElement("div");
      el.style.position = "absolute";
      el.style.width = "0";
      el.style.height = "0";
      el.style.pointerEvents = "none";
      el.style.left = "0";
      el.style.top = "0";
      document.body.appendChild(el);
      anchorEl.value = el;
    });
    onBeforeUnmount(() => {
      window.removeEventListener("keydown", onGlobalKeyDownCapture, true);
      window.removeEventListener(
        "pointerdown",
        onGlobalPointerDownCapture,
        true
      );
      if (anchorEl.value && anchorEl.value.parentNode) {
        anchorEl.value.parentNode.removeChild(anchorEl.value);
      }
      anchorEl.value = undefined;
    });
    return {
      contextMenuEl,
      triggerEl,
      anchorEl,
      currentAttendance,
      renderedSections,
      hide,
      isDisplayed,
      shortcuts,
      show,
      style,
    };
  },
});
</script>
<style scoped lang="scss">
@use "@/styles/context-actions.scss";

.right-click-menu {
  position: fixed;
  z-index: $z-index-modal;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-style: solid;
  border-width: 1px;
  box-shadow: $tooltip;
  border-radius: 10px;
  @include themed() {
    background-color: t($bg-event);
    border-color: t($grey-15);
  }

  .separator {
    width: 100%;
    height: 1px;
    @include themed() {
      background-color: t($grey-15);
    }
  }

  .section {
    padding: $padding-tooltip;
    display: flex;
    flex-direction: column;
    row-gap: 2px;
  }
}
</style>
